import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.#filterMediaByVariantTags(newMediaGallery, event.detail.resource);

    this.replaceWith(newMediaGallery);
  };

  /**
   * Extracts the variant group tags written in an image's alt text,
   * e.g. "輪圈近攝 #黑 #Black" -> ['黑', 'black'].
   *
   * @param {Element} container - An element that contains (or is) the media image.
   * @returns {string[]} The lowercase tags found in the alt text.
   */
    #extractTags = (container) => {
    const img =
      container.tagName === 'IMG'
        ? container
        : container.querySelector('img');

    const alt = img?.getAttribute('alt') || '';

    // 支援 #"Sky Blue"、#黑、#Black
    const pattern = /#"([^"]+)"|#([^\s#"]+)/g;

    return Array.from(alt.matchAll(pattern))
      .map((match) => (match[1] || match[2]).trim().toLowerCase())
      .filter(Boolean);
  };


  /**
   * Filters the gallery media by the variant group tags written in each media's
   * alt text. Media without any tag is treated as shared media and always stays
   * visible. Media is shown when at least one of its tags matches one of the
   * selected variant's option values (case-insensitive).
   *
   * @param {HTMLElement} gallery - The freshly rendered media gallery element.
   * @param {Object} variant - The selected variant JSON from the variant picker.
   */
  #filterMediaByVariantTags = (gallery, variant) => {
    if (!gallery) return;

    const selectedValues = new Set(
      (variant?.options || []).map((value) => String(value).trim().toLowerCase())
    );
    if (selectedValues.size === 0) return;

    const shouldShow = (container) => {
      const tags = this.#extractTags(container);
      // Untagged media belongs to the shared group and is always visible.
      if (tags.length === 0) return true;
      return tags.some((tag) => selectedValues.has(tag));
    };

    // Theme CSS rules (e.g. display: grid) override the `hidden` attribute, so
    // inline styles are used to guarantee the media is visually removed.
    const setHidden = (element, hide) => {
      element.style.display = hide ? 'none' : '';
    };

    // 1. Carousel slides (also used on mobile)
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    slides.forEach((slide) => {
      setHidden(slide, !shouldShow(slide));
    });

    // 2. Desktop grid items
    gallery.querySelectorAll(':scope > .media-gallery__grid > li').forEach((item) => {
      setHidden(item, !shouldShow(item));
    });

    // 3. Slideshow control thumbnails (order matches the slides)
    const hiddenFlags = slides.map((slide) => slide.style.display === 'none');
    gallery.querySelectorAll('slideshow-controls').forEach((controls) => {
      controls.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail, index) => {
        if (index < hiddenFlags.length) setHidden(thumbnail, hiddenFlags[index]);
      });

      controls.querySelectorAll('.slideshow-controls__dots > li').forEach((dotItem, index) => {
        if (index < hiddenFlags.length) setHidden(dotItem, hiddenFlags[index]);
      });
    });

    // 4. Zoom dialog media and its thumbnails (separate lists with their own images)
    gallery.querySelectorAll('.dialog-zoomed-gallery > li').forEach((item) => {
      setHidden(item, !shouldShow(item));
    });
    gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach((thumbnail) => {
      setHidden(thumbnail, !shouldShow(thumbnail));
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

// Variant tag filter enabled.
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
